import { useEffect, useMemo, useState } from "react";
import { FaTimes } from "react-icons/fa";
import AppIcon from "../AppIcon";
import { useProfileEditorAppCatalog } from "../../hooks/useProfileEditorAppCatalog";
import "../../pages/profileEditor.css";

/**
 * Multi-Select App-Picker als Modal.
 * Wenn `installedApps` gesetzt ist: nur diese (Inventar der Schülergeräte).
 */
export default function AppPickerModal({
  open,
  title = "Apps auswählen",
  value = [],
  onChange,
  onClose,
  confirmLabel = "Übernehmen",
  installedApps = null,
  emptyHint = "Keine Apps gefunden.",
}) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(() => new Set(value || []));
  const bundleIds = useMemo(() => {
    const ids = new Set(value || []);
    for (const app of installedApps || []) {
      if (app?.bundleId) ids.add(app.bundleId);
    }
    return [...ids];
  }, [value, installedApps]);
  const { appsById } = useProfileEditorAppCatalog(bundleIds);

  useEffect(() => {
    if (open) setSelected(new Set(value || []));
  }, [open, value]);

  const pickerApps = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = (name, id) => !q || String(name).toLowerCase().includes(q) || String(id).toLowerCase().includes(q);
    const source = Array.isArray(installedApps) ? installedApps : [];
    return source
      .filter((app) => app?.bundleId && matches(app.name || app.bundleId, app.bundleId))
      .map((app) => ({
        id: app.bundleId,
        name: app.name || appsById.get(app.bundleId)?.name || app.bundleId,
        artworkUrl: appsById.get(app.bundleId)?.artworkUrl || "",
        deviceCount: app.deviceCount,
        subtitle: app.deviceCount ? `auf ${app.deviceCount} Gerät(en)` : app.bundleId,
      }));
  }, [query, installedApps, appsById]);

  if (!open) return null;

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedList = [...selected];
  const labelFor = (id) => appsById.get(id)?.name
    || installedApps?.find((a) => a.bundleId === id)?.name
    || id;

  return (
    <div className="teacher-modal-backdrop" role="dialog" aria-modal="true">
      <div className="teacher-modal">
        <div className="teacher-modal__head">
          <h3>{title}</h3>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose} aria-label="Schließen">
            <FaTimes />
          </button>
        </div>
        <p className="small text-muted px-3 mb-1">
          Nur Apps, die auf Schülergeräten inventarisiert sind (inkl. vom Nutzer installierte).
        </p>
        <div className="px-3">
          <input
            className="form-control mb-2"
            placeholder="App suchen…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </div>
        {selectedList.length ? (
          <div className="teacher-modal__chips mb-2 px-3">
            {selectedList.map((id) => (
              <button key={id} type="button" className="teacher-modal__chip" onClick={() => toggle(id)}>
                <AppIcon src={appsById.get(id)?.artworkUrl} name={labelFor(id)} size={22} />
                <span>{labelFor(id)}</span>
                <FaTimes />
              </button>
            ))}
          </div>
        ) : null}
        <div className="teacher-modal__body">
          {!pickerApps.length ? (
            <p className="text-muted mb-0">{emptyHint}</p>
          ) : (
            <div className="app-picker-grid">
              {pickerApps.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`app-picker-item ${selected.has(item.id) ? "is-selected" : ""}`}
                  onClick={() => toggle(item.id)}
                  title={item.subtitle || item.id}
                >
                  <AppIcon src={item.artworkUrl} name={item.name} size={44} />
                  <span>{item.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="teacher-modal__foot">
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Abbrechen</button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              onChange?.(selectedList);
              onClose?.();
            }}
          >
            {confirmLabel} ({selectedList.length})
          </button>
        </div>
      </div>
    </div>
  );
}

/** Variante: Auswahl nach adamId für VPP-Installation (Admin). */
export function VppAppInstallModal({
  open,
  title = "Apps installieren",
  onConfirm,
  onClose,
}) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const { vppApps } = useProfileEditorAppCatalog();

  const catalog = useMemo(() => {
    const q = query.trim().toLowerCase();
    return vppApps.filter((app) => {
      if (!app.adamId || !app.bundleId) return false;
      if (!q) return true;
      return String(app.name || "").toLowerCase().includes(q)
        || String(app.bundleId).toLowerCase().includes(q)
        || String(app.adamId).includes(q);
    });
  }, [vppApps, query]);

  if (!open) return null;

  const toggle = (adamId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(adamId)) next.delete(adamId);
      else next.add(adamId);
      return next;
    });
  };

  return (
    <div className="teacher-modal-backdrop" role="dialog" aria-modal="true">
      <div className="teacher-modal">
        <div className="teacher-modal__head">
          <h3>{title}</h3>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}><FaTimes /></button>
        </div>
        <input className="form-control mb-2 mx-3" style={{ width: "auto" }} placeholder="App suchen…" value={query} onChange={(e) => setQuery(e.target.value)} />
        <div className="teacher-modal__body">
          <div className="app-picker-grid">
            {catalog.map((app) => (
              <button
                key={app.adamId}
                type="button"
                className={`app-picker-item ${selected.has(String(app.adamId)) ? "is-selected" : ""}`}
                onClick={() => toggle(String(app.adamId))}
                title={app.bundleId}
              >
                <AppIcon src={app.artworkUrlSmall || app.artworkUrl} name={app.name} size={44} />
                <span>{app.name || app.bundleId}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="teacher-modal__foot">
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Abbrechen</button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!selected.size}
            onClick={() => {
              onConfirm?.([...selected]);
              onClose?.();
            }}
          >
            Installieren ({selected.size})
          </button>
        </div>
      </div>
    </div>
  );
}
